import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import moment from 'moment';
import ApiService from '../services/ApiService';

const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// Per-session plans (exactly one session) have no term to extend — they are
// consumed by a booking. Mirrors backend helpers/stripe.js isPerSessionPlan.
const isPerSessionPlan = (plan) =>
    !!plan && plan.is_unlimited_sessions === false && Number(plan.no_sessions) === 1;

// Upper bound per unit (~10 years). Mirrors extendSubscriber on the backend.
const MAX_BY_UNIT = { days: 3650, months: 120, years: 10 };

/** Whether the admin can move this membership's expiry date. */
export function canExtendMembership(sub) {
    return !!sub
        && sub.approval_status === 'Approved'
        && sub.status !== 'Cancelled'
        && sub.cancellation_status !== 'Cancelled'
        && !isPerSessionPlan(sub.plan_id);
}

/**
 * Opens the Extend Membership dialog for `sub` (a subscriber row with plan_id
 * and user_id populated): either add days/months/years on top of the current expiry
 * (from today if it has already lapsed) or pick an exact new expiry date.
 * Shared by the Subscribers list and the customer's Membership History.
 * Resolves true once the server has saved the new date.
 */
export async function extendMembership(sub) {
    if (!sub?._id) return false;

    const current = sub.expire_at ? moment(sub.expire_at) : null;
    const lapsed = !current || !current.isAfter(moment());
    // Added time counts from the current expiry, or from today once it has passed.
    const base = lapsed ? moment() : current;
    const tomorrow = moment().add(1, 'day').format('YYYY-MM-DD');

    const result = await Swal.fire({
        title: 'Extend Membership',
        html: `
            <div class="text-start">
                <div class="mb-4 fs-6">
                    <div><b>${escapeHtml(sub.user_id?.name || 'Member')}</b> · ${escapeHtml(sub.plan_id?.title || 'Membership')}</div>
                    <div class="text-muted fs-7">
                        Current expiry: <b>${current ? current.format('DD MMM YYYY') : '—'}</b>
                        ${lapsed && current ? ' <span class="badge badge-light-danger ms-1">Expired</span>' : ''}
                    </div>
                </div>

                <div class="d-flex gap-6 mb-4">
                    <label class="form-check form-check-custom form-check-solid">
                        <input class="form-check-input" type="radio" name="ext-mode" value="add" checked />
                        <span class="form-check-label fw-semibold">Add time</span>
                    </label>
                    <label class="form-check form-check-custom form-check-solid">
                        <input class="form-check-input" type="radio" name="ext-mode" value="date" />
                        <span class="form-check-label fw-semibold">Choose date</span>
                    </label>
                </div>

                <div id="ext-add" class="d-flex gap-3">
                    <input id="ext-amount" type="number" min="1" value="1" class="form-control form-control-solid" style="max-width: 110px" />
                    <select id="ext-unit" class="form-select form-select-solid">
                        <option value="days">Day(s)</option>
                        <option value="months">Month(s)</option>
                        <option value="years" selected>Year(s)</option>
                    </select>
                </div>

                <div id="ext-date" style="display: none">
                    <input id="ext-expire" type="date" min="${tomorrow}" value="${base.clone().add(1, 'year').format('YYYY-MM-DD')}" class="form-control form-control-solid" />
                </div>

                <div class="mt-4 fs-7 text-muted">New expiry: <b id="ext-preview" class="text-dark"></b></div>
                ${lapsed && current ? '<div class="mt-2 fs-8 text-muted">Extending an expired membership makes it active again.</div>' : ''}
            </div>
        `,
        showCancelButton: true,
        confirmButtonColor: '#4BC95F',
        confirmButtonText: 'Extend',
        cancelButtonColor: '#9E9B9B',
        cancelButtonText: 'Cancel',
        focusConfirm: false,
        didOpen: (popup) => {
            const $ = (sel) => popup.querySelector(sel);
            const mode = () => popup.querySelector('input[name="ext-mode"]:checked').value;
            const refresh = () => {
                $('#ext-add').style.display = mode() === 'add' ? 'flex' : 'none';
                $('#ext-date').style.display = mode() === 'date' ? 'block' : 'none';
                let next = null;
                if (mode() === 'add') {
                    const qty = parseInt($('#ext-amount').value, 10);
                    if (qty >= 1) next = base.clone().add(qty, $('#ext-unit').value);
                } else if ($('#ext-expire').value) {
                    next = moment($('#ext-expire').value, 'YYYY-MM-DD');
                }
                $('#ext-preview').textContent = next ? next.format('DD MMM YYYY') : '—';
            };
            popup.querySelectorAll('input, select').forEach((el) => {
                el.addEventListener('input', refresh);
                el.addEventListener('change', refresh);
            });
            refresh();
        },
        preConfirm: () => {
            const popup = Swal.getPopup();
            const mode = popup.querySelector('input[name="ext-mode"]:checked').value;
            if (mode === 'add') {
                const amount = parseInt(popup.querySelector('#ext-amount').value, 10);
                const unit = popup.querySelector('#ext-unit').value;
                if (!(amount >= 1 && amount <= MAX_BY_UNIT[unit])) {
                    Swal.showValidationMessage(`Enter between 1 and ${MAX_BY_UNIT[unit]} ${unit}`);
                    return false;
                }
                return { amount, unit };
            }
            const expire_at = popup.querySelector('#ext-expire').value;
            if (!expire_at || expire_at < tomorrow) {
                Swal.showValidationMessage('Choose a date after today');
                return false;
            }
            return { expire_at };
        },
    });
    if (!result.isConfirmed) return false;

    try {
        const res = await ApiService.request({
            method: 'POST',
            url: `admin/membership-plans/subscribers/${sub._id}/extend`,
            data: result.value,
        });
        if (res.data.status) {
            toast.success(res.data.message || 'Membership extended');
            return true;
        }
        toast.error(res.data.message || 'Failed to extend membership');
    } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to extend membership');
    }
    return false;
}
